import { withRetry } from './utils/retry';
// The app's single backend. Every write below goes through it rather than
// the SDK, so the PocketBase adapter can answer the same calls - including
// the question the retry policy asks, `errorKind`.
import { backend } from './backend/app';
import type { BackendErrorKind, CollectionRead, Unsubscribe } from './backend/types';
import { toMs } from './utils/timestamps';
import { fnv1aHex } from './utils/fnv1a';
import { diag, errorSummary } from './diagnostics/log';
import { resilientListener } from './resilientListener';
import type { PermissionsDoc } from './engine/settings/permissions';
import { withHouseRule, withLock, withOverride } from './engine/settings/mutate';
import type { Target, TurnTrackerState, HistorySnapshot, SharedHistoryUpdate, NarrativeEntryDoc } from './types';
import type { Character } from './engine/character/types';
import {
  libraryDocId,
  splitLibraryEntry,
  type LibraryEntry,
  partDocId,
  assembleLibrary,
  type LibraryDoc,
} from './engine/battlefield/mapLibrarySync';
import { castDocId } from './engine/scenes/sceneSync';
import { isMoveLineOf } from './engine/moveBeat';

// ---- Resilient listeners ----
// The error policy and the app notices live in resilientListener.ts; the
// handler setters are re-exported so the app keeps one import.
export {
  setListenerFailureHandler,
  setConnectionNoticeHandler,
  setSessionRevokedHandler,
  setAuthExpiredHandler,
  reportWriteFailure,
} from './resilientListener';

/**
 * The retry policy every write below uses: a hiccup waits and tries again,
 * a throttle waits longer, and a refusal, a dead sign-in or a bad request
 * stops at once. The kind comes from the adapter; the delays are the app's.
 */
const RETRY_BASE_MS = 500;
const THROTTLED_BASE_MS = 2000;
function retryDelay(err: unknown, attempt: number): number | null {
  const kind = backend.errorKind(err);
  const delay =
    kind === 'transient'
      ? RETRY_BASE_MS * 2 ** attempt
      : kind === 'throttled'
        ? THROTTLED_BASE_MS * 2 ** attempt
        : null;
  // SAID, not swallowed (v8.362.2): a retried write used to leave no trace,
  // so a table being throttled looked like nothing at all in its own log.
  if (delay !== null) diag.warn('sync', `write ${kind} - retry ${attempt + 1} in ${delay}ms`, errorSummary(err));
  return delay;
}
const retried = <T>(fn: () => Promise<T>): Promise<T> => withRetry(fn, retryDelay);

/** What kind of failure an error is, in the port's words - for a caller with its own retry policy. */
export const errorKind = (err: unknown): BackendErrorKind => backend.errorKind(err);

// ---- Sessions ----

/**
 * The session lifecycle lives in the adapter now (v8.103.0).
 *
 * Every behaviour moved with it: the crypto join code, the schemaVersion
 * stamp, the tolerated joinCodes write and the legacy query fallback. It
 * belongs there because the awkward parts are Firestore's - a `members` MAP
 * merged field-wise, an auto-generated document id, and a rules-transition
 * fallback that a PocketBase deployment needs none of.
 *
 * Re-exported here so the existing callers and their tests keep working; the
 * facade goes when this module does.
 */
export const createSession = (displayName: string) => backend.sessions.create(displayName);
export const joinSession = (joinCode: string, displayName: string) => backend.sessions.join(joinCode, displayName);
export const validateSession = (sessionId: string) => backend.sessions.validate(sessionId);
export const rejoinSession = (sessionId: string, displayName: string) =>
  backend.sessions.rejoin(sessionId, displayName);

// ---- Narrative ----

/**
 * A collection listener hands over a READ, never a snapshot.
 *
 * `hasPendingWrites` and `docChanges()` are Firestore's words for "is this the
 * whole truth" and "who is genuinely gone", and four production bugs came from
 * consumers having to know them. `readAsCollection` answers both once, in the
 * adapter, and nothing above here can get the documents without the answers.
 */
type CollectionCallback = (read: CollectionRead<Record<string, unknown>>) => void;

/**
 * Subscribe to a collection and deliver it as a read.
 *
 * `includeMetadataChanges` is REQUIRED: without it onSnapshot fires only on
 * content changes, so the acknowledgement of your own write - a metadata-only
 * transition - never arrives and `listsEveryone` stays false forever (see
 * gotchas.md v8.95.0). Consumers are idempotent, so the extra callbacks cost
 * nothing.
 */
/**
 * THE E2E SEAMS (v8.362.3). A spec cannot cut a browser's network; it can
 * ask this module to drop every delivery (a stale client, exactly as a
 * missed event leaves one) and to fire the reconnect listeners (what the
 * adapter does after a real one). `main.jsx` puts them on the window under
 * the emulator build only; a production build never calls either.
 */
let e2eMuted = false;
export function __e2eSetMuted(on: boolean): void {
  e2eMuted = on;
}
const reconnectListeners = new Set<() => void>();
export function __e2eFireReconnect(): void {
  for (const l of reconnectListeners) l();
}

/**
 * Each muted listener's latest dropped read, by its callback. The mute
 * alone is a MISSED event (e2e/sync-chaos proves the roster audit heals
 * one); `__e2eRedeliver` adds what a real reconnect brings besides - the
 * current state, which Firestore's SDK hands over and PocketBase reseeds.
 * The browser fuzzer unmutes with both: without it every stale document
 * but the roster stayed stale, a state no real client reaches (v8.393.14).
 */
const dropped = new Map<(read: never) => void, unknown>();
export function __e2eRedeliver(): void {
  const missed = [...dropped];
  dropped.clear();
  for (const [cb, read] of missed) cb(read as never);
}

/** A delivery proves the listener healthy; then the E2E mute, when on, drops it. */
const delivered =
  <R>(onHealthy: () => void, cb: (read: R) => void) =>
  (read: R): void => {
    onHealthy();
    if (e2eMuted) {
      dropped.set(cb, read);
      return;
    }
    dropped.delete(cb);
    cb(read);
  };

/** A stopped listener's missed read is nobody's to redeliver. */
const forgetOnStop =
  (cb: (read: never) => void, unsub: Unsubscribe): Unsubscribe =>
  () => {
    dropped.delete(cb);
    unsub();
  };

const watchCollection = (label: string, path: string, callback: CollectionCallback): Unsubscribe =>
  forgetOnStop(
    callback,
    resilientListener(label, (onError, onHealthy) =>
      backend.data.watchCollection<Record<string, unknown>>(path, delivered(onHealthy, callback), onError),
    ),
  );

/**
 * An audience-scoped collection (v8.363.0): the records that name `anyOf`.
 * A refusal is an answer, not an outage - said once as an empty view, never
 * retried (`onWaitingRoomsUpdate` set the pattern).
 */
const watchCollectionWhere = (
  label: string,
  path: string,
  filter: import('./backend/types').ArrayFilter,
  callback: CollectionCallback,
): Unsubscribe =>
  forgetOnStop(
    callback,
    resilientListener(label, (onError, onHealthy) =>
      backend.data.watchCollectionWhere<Record<string, unknown>>(
        path,
        filter,
        delivered(onHealthy, callback),
        (err: unknown) => {
          if (backend.errorKind(err) !== 'denied') return onError(err);
          diag.warn('sync', `${label}: refused - an empty view, not a retry`, errorSummary(err));
          callback({ entries: [], docs: [], listsEveryone: false, removed: [] });
        },
      ),
    ),
  );

/** Subscribe to one document and deliver it as a read. */
const watchDocument = <T>(label: string, path: string, cb: (read: { exists: boolean; data: T | null }) => void) =>
  forgetOnStop(
    cb,
    resilientListener(label, (onError, onHealthy) => backend.data.watchDoc<T>(path, delivered(onHealthy, cb), onError)),
  );

/**
 * The `{ data: "<json>" }` document, which is most of `shared/*`.
 *
 * Five listeners spelled this out identically: parse the field, hand over the
 * value and `true`, and on anything unreadable hand over `null` and `false`.
 * A corrupt document is reported as absent rather than thrown, because one
 * unparseable blob must not take the session down with it.
 */
const watchJsonDoc = <T>(label: string, path: string, callback: SharedDocCallback<T>): Unsubscribe =>
  watchDocument<{ data?: string }>(label, path, (read) => {
    if (!read.exists || !read.data) return callback(null, false);
    try {
      callback(JSON.parse(read.data.data as string) as T, true);
    } catch {
      callback(null, false);
    }
  });

export const onNarrativeUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe => {
  return watchCollection('narrative', `sessions/${sessionId}/narrative`, callback);
};

// ---- The vox (v8.363.3) ----

/**
 * The lines that name me: everyone's, every player's (not for the GM), mine.
 * The query is what the Firestore rules can prove; PocketBase applies the
 * same on the way in.
 */
export const onChatUpdate = (
  sessionId: string,
  uid: string,
  isGm: boolean,
  callback: CollectionCallback,
): Unsubscribe =>
  watchCollectionWhere(
    'chat',
    `sessions/${sessionId}/chat`,
    { field: 'audience', anyOf: isGm ? ['*', uid] : ['*', '@players', uid] },
    callback,
  );

export const addChatMessage = async (sessionId: string, id: string, doc: Record<string, unknown>): Promise<void> => {
  diag.info('chat', `send ${String(doc.kind)} → ${String(doc.conv)}`);
  await retried(() => backend.data.setDoc(`sessions/${sessionId}/chat/${id}`, doc));
};

/** A tombstone, not a deletion: the line stays in the stream as "message removed". */
export const tombstoneChatMessage = async (sessionId: string, id: string): Promise<void> => {
  await retried(() =>
    backend.data.setDoc(
      `sessions/${sessionId}/chat/${id}`,
      { kind: 'removed', text: '', mentions: [], roll: '' },
      { merge: true },
    ),
  );
};

export const onChannelsUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe =>
  watchCollection('chatChannels', `sessions/${sessionId}/chatChannels`, callback);

export const setChannel = async (sessionId: string, id: string, doc: Record<string, unknown>): Promise<void> => {
  await retried(() => backend.data.setDoc(`sessions/${sessionId}/chatChannels/${id}`, doc));
};

export const deleteChannel = async (sessionId: string, id: string): Promise<void> => {
  await retried(() => backend.data.deleteDoc(`sessions/${sessionId}/chatChannels/${id}`));
};

// ---- Handouts (v8.363.9) ----

/**
 * What the GM shows me - or, for the GM, everything the session holds
 * including drafts. The player's query is the audience the rules can prove;
 * the GM's is the plain collection, which only they may list.
 */
export const onHandoutsUpdate = (sessionId: string, uid: string | null, callback: CollectionCallback): Unsubscribe =>
  uid === null
    ? watchCollection('handouts', `sessions/${sessionId}/handouts`, callback)
    : watchCollectionWhere(
        'handouts',
        `sessions/${sessionId}/handouts`,
        // A reveal can be taken back: the view narrows, and PocketBase says
        // nothing to whoever it excludes - so this one re-lists on a timer.
        { field: 'audience', anyOf: ['*', '@players', uid], narrows: true },
        callback,
      );

export const setHandout = async (
  sessionId: string,
  id: string,
  doc: Record<string, unknown>,
  merge = false,
): Promise<void> => {
  diag.info('handouts', `${merge ? 'patch' : 'set'} ${id}`);
  await retried(() =>
    backend.data.setDoc(`sessions/${sessionId}/handouts/${id}`, doc, merge ? { merge: true } : undefined),
  );
};

export const deleteHandout = async (sessionId: string, id: string): Promise<void> => {
  diag.info('handouts', `delete ${id}`);
  await retried(() => backend.data.deleteDoc(`sessions/${sessionId}/handouts/${id}`));
};

/** The files port, by handout. Not retried: a failed upload is the caller's sentence. */
export const uploadHandoutFile = async (
  sessionId: string,
  id: string,
  field: 'file' | 'thumb',
  blob: Blob,
  opts: { name: string; contentType: string },
): Promise<string> => {
  diag.info('files', `upload ${id}.${field} ${opts.contentType} ${blob.size} B`);
  return backend.files.upload(`sessions/${sessionId}/handouts/${id}`, field, blob, opts);
};

export const handoutFileUrl = (sessionId: string, id: string, field: 'file' | 'thumb', ref: string): Promise<string> =>
  backend.files.url(`sessions/${sessionId}/handouts/${id}`, field, ref);

export const deleteHandoutFile = async (
  sessionId: string,
  id: string,
  field: 'file' | 'thumb',
  ref: string,
): Promise<void> => {
  diag.info('files', `delete ${id}.${field}`);
  await backend.files.delete(`sessions/${sessionId}/handouts/${id}`, field, ref);
};

/**
 * The document id of a narrative entry: `${timestamp}-${digest}`.
 *
 * DETERMINISTIC, so a retried write overwrites the same document instead of
 * duplicating it, and distinct per author and kind within one millisecond.
 *
 * NO USER TEXT IN IT (2026-10-02). It used to carry the first 8 characters
 * of the display name, and a name holding "/" (`<b>x</b>`) split the
 * document path: PocketBase answered 400 "Missing collection context" and
 * every log line from that player was refused (Firestore reads it as a
 * nested path too). The digest of who and what keeps both properties with
 * nothing a name can break. Nothing reads the id's shape - the log sorts by
 * `timestamp` - so entries written under the old ids stay as they were.
 */
export function narrativeDocId(entry: NarrativeEntryDoc): string {
  const ts = entry.timestamp || Date.now();
  return `${ts}-${fnv1aHex(`${entry.userName ?? ''}\u0000${entry.type ?? ''}`)}`;
}

export const addNarrativeEntry = (sessionId: string, entry: NarrativeEntryDoc): Promise<void> =>
  retried(() =>
    backend.data.setDoc(`sessions/${sessionId}/narrative/${narrativeDocId(entry)}`, {
      ...entry,
      timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
    }),
  );

// ---- Targets ----

// ---- Combatants (v7.0.0 roster) ----
//
// One document per combatant, with the two halves in SEPARATE FIELDS so
// they can be written independently:
//
//   sheet / overrides / loadouts  - JSON strings, COLD (edited deliberately)
//   state                         - a NATIVE MAP, HOT (every wound tick)
//
// `state` is native rather than a JSON string on purpose: that is what
// lets a wound tick (updateDoc {state}) and a concurrent talent edit
// (updateDoc {sheet}) merge server-side on disjoint fields instead of
// clobbering each other. Its members are all Firestore-legal - string[],
// Record<string, number>, Record<string, object>, an array of maps - and
// there are no nested arrays.

interface CombatantDoc {
  [key: string]: unknown;
  schemaVersion: number;
  name: string;
  kind: string;
  order: number;
  sheet: string;
  overrides: string;
  loadoutRef: { id: string; name: string } | null;
  slotOrder: (string | null)[] | null;
  state: Record<string, unknown>;
  updatedAt: unknown;
  updatedBy: string | null;
}

export const onCombatantsUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe => {
  return watchCollection('combatants', `sessions/${sessionId}/combatants`, callback);
};

/** One read of the whole collection, for the roster audit (v8.362.1) - what the server holds right now. */
export const listCombatants = (sessionId: string): Promise<{ id: string; data: Record<string, unknown> }[]> =>
  backend.data.listDocs<Record<string, unknown>>(`sessions/${sessionId}/combatants`);

/** The backend's reconnection, for whoever should re-check after one (v8.362.1). */
export const onReconnect = (listener: () => void): Unsubscribe => {
  reconnectListeners.add(listener);
  const off = backend.onReconnect(listener);
  return () => {
    reconnectListeners.delete(listener);
    off();
  };
};

/** Full write - creation, or a wholesale replace after migration/import. */
export const createCombatant = (sessionId: string, id: string, payload: CombatantDoc): Promise<void> =>
  retried(() => backend.data.setDoc(`sessions/${sessionId}/combatants/${id}`, payload as Record<string, unknown>));

/**
 * One half of a combatant, merged - and retried ONLY WHILE THE SCREEN STILL
 * HOLDS THE COMBATANT (2026-10-01). A retry waits out a dropped connection,
 * and the combatant can be deleted meanwhile: the retry then landed after
 * the delete, the upsert re-created the document, and a removed target came
 * back on every screen. `stillWanted` is asked before every attempt; absent,
 * every attempt is wanted.
 *
 * ONTO A COMBATANT THAT MUST EXIST, AN UPDATE (2026-10-17). A screen that
 * still held someone the GM had just struck wrote a half before it heard,
 * and the merge re-created the document holding only that half: a sheetless
 * stub, left on the server for good. `mustExist` (the screen has seen the
 * combatant whole, so the document existed) makes the write an `updateDoc`,
 * which writes nothing onto a document that is gone. Only a fresh add, whose
 * first half creates the document, still merges.
 */
const writeCombatantHalf = (
  path: string,
  payload: () => Record<string, unknown>,
  stillWanted?: () => boolean,
  mustExist?: () => boolean,
): Promise<void> =>
  retried(async () => {
    if (stillWanted && !stillWanted()) return;
    if (mustExist?.()) await backend.data.updateDoc(path, payload());
    else await backend.data.setDoc(path, payload(), { merge: true });
  });

/** HOT path: combat state only. Never rewrites the fat sheet JSON. */
export const setCombatantState = (
  sessionId: string,
  id: string,
  state: Record<string, unknown>,
  playerId: string | null,
  stillWanted?: () => boolean,
  mustExist?: () => boolean,
): Promise<void> =>
  writeCombatantHalf(
    `sessions/${sessionId}/combatants/${id}`,
    () => ({ state, updatedAt: new Date(), updatedBy: playerId }),
    stillWanted,
    mustExist,
  );

/**
 * HOT path, FIELD BY FIELD (plan state-field-merge, 2026-10-24): only the state's keys this screen changed, merged
 * into the server's copy, onto a document that exists - so two seats changing different fields of one combatant at
 * once both land. A document gone meanwhile is left gone. The whole state still goes by `setCombatantState` for a
 * fresh add and a re-send.
 */
export const mergeCombatantState = (
  sessionId: string,
  id: string,
  patch: { set: Record<string, unknown>; del: string[] },
  playerId: string | null,
  stillWanted?: () => boolean,
): Promise<void> =>
  retried(async () => {
    if (stillWanted && !stillWanted()) return;
    await backend.data.mergeFields(`sessions/${sessionId}/combatants/${id}`, 'state', patch, {
      updatedAt: new Date(),
      updatedBy: playerId,
    });
  });

/** COLD path: the sheet half. */
export const setCombatantSheet = (
  sessionId: string,
  id: string,
  payload: Partial<CombatantDoc>,
  playerId: string | null,
  stillWanted?: () => boolean,
  mustExist?: () => boolean,
): Promise<void> =>
  writeCombatantHalf(
    `sessions/${sessionId}/combatants/${id}`,
    () => ({ ...payload, updatedAt: new Date(), updatedBy: playerId }) as Record<string, unknown>,
    stillWanted,
    mustExist,
  );

export const deleteCombatant = (sessionId: string, id: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(`sessions/${sessionId}/combatants/${id}`));

/** Undo: restore many combat states at once, touching no sheets. */
export const batchRestoreCombatantStates = (
  sessionId: string,
  updates: { id: string; state: Record<string, unknown> }[],
): Promise<void> =>
  retried(() =>
    backend.data.batch(
      updates.map((u) => ({
        op: 'set' as const,
        path: `sessions/${sessionId}/combatants/${u.id}`,
        data: { state: u.state },
        merge: true,
      })),
    ),
  );

/** Migration + session import: write a whole roster in one transaction. */
export const batchWriteCombatants = (sessionId: string, payloads: { id: string; doc: CombatantDoc }[]): Promise<void> =>
  retried(() =>
    backend.data.batch(
      payloads.map((p) => ({
        op: 'set' as const,
        path: `sessions/${sessionId}/combatants/${p.id}`,
        data: p.doc as unknown as Record<string, unknown>,
      })),
    ),
  );

// ---- Loadouts (shared weapon kits) ----

export const onLoadoutsUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe => {
  return watchCollection('loadouts', `sessions/${sessionId}/loadouts`, callback);
};

export const setLoadout = (sessionId: string, id: string, loadout: unknown, playerId: string | null): Promise<void> =>
  retried(() =>
    backend.data.setDoc(`sessions/${sessionId}/loadouts/${id}`, {
      data: JSON.stringify(loadout),
      name: (loadout as { name?: string })?.name ?? 'Loadout',
      updatedAt: new Date(),
      updatedBy: playerId,
    }),
  );

export const deleteLoadout = (sessionId: string, id: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(`sessions/${sessionId}/loadouts/${id}`));

// ---- Effect presets (the table's palette, v8.199.2; any kind since v8.370.1) ----
//
// The collection keeps its first name, `afflictionPresets` - a rename
// would strand every table's saved payloads. A row's `kind` says what the
// spec is; a row written before v8.370.1 has none and reads as an
// affliction.

export type EffectPresetKind = 'affliction' | 'exposure' | 'hallucinogen' | 'area';

export const onEffectPresetsUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe => {
  return watchCollection('afflictionPresets', `sessions/${sessionId}/afflictionPresets`, callback);
};

export const setEffectPreset = (
  sessionId: string,
  id: string,
  preset: { kind: EffectPresetKind; spec: unknown },
  playerId: string | null,
): Promise<void> =>
  retried(() =>
    backend.data.setDoc(`sessions/${sessionId}/afflictionPresets/${id}`, {
      kind: preset.kind,
      // Stringified - a json column would reorder keys under echo prevention.
      spec: JSON.stringify(preset.spec),
      name: (preset.spec as { name?: string })?.name ?? 'Effect',
      updatedAt: new Date(),
      updatedBy: playerId,
    }),
  );

// ---- Players ----

export const onPlayersUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe => {
  return watchCollection('players', `sessions/${sessionId}/players`, callback);
};

/**
 * Write fields onto MY SEAT - heartbeat, acting-as, diagnostics. Resolves
 * false when the seat is gone (the GM's prune took it); the caller decides
 * what that means. Only joining makes a seat, with every field it needs:
 * this was a merge set, and one queued offline put a pruned seat back with
 * no name on either backend - "Kick ?" on the GM's list (2026-10-03).
 */
export const setPlayerProfile = (
  sessionId: string,
  playerId: string,
  profileData: Record<string, unknown>,
): Promise<boolean> => backend.data.updateDoc(`sessions/${sessionId}/players/${playerId}`, profileData);

// ---- Shared attacker profiles ----

/** A shared document's watch: its value, and whether it EXISTS (a failed read never reaches it: it is not `ok`). */
export type SharedDocCallback<T> = (data: T | null, exists: boolean) => void;

// ---- Shared target templates ----

export const setSharedTgtTemplates = (sessionId: string, tgtTemplates: Record<string, Target>): Promise<void> =>
  retried(() => {
    return backend.data.setDoc(`sessions/${sessionId}/shared/tgtTemplates`, { data: JSON.stringify(tgtTemplates) });
  });

export const onSharedTgtTemplatesUpdate = (
  sessionId: string,
  callback: SharedDocCallback<Record<string, Target>>,
): Unsubscribe => {
  return watchJsonDoc('shared/tgtTemplates', `sessions/${sessionId}/shared/tgtTemplates`, callback);
};

// ---- SHARED UNDO/REDO HISTORY ----

const MAX_SHARED_HISTORY = 30;
/**
 * The history document's size budget in UTF-8 bytes - under Firestore's
 * 1 MiB field limit (1,048,487) with room for the wrapper. On a big table
 * thirty snapshots of every combatant's state pass the limit; the server
 * then refuses every push and Undo stops working for everyone (v8.391.3,
 * found by ./dev fuzz). Over the budget, the OLDEST entries go: a big
 * table gets a shorter undo, never a broken one. One document per entry
 * would lift the cap altogether (backlog).
 */
const HISTORY_BYTES_BUDGET = 900_000;
const utf8Bytes = (text: string) => new TextEncoder().encode(text).length;

/** Drop the oldest entries until the serialized stack fits the budget, the pointer following its entry. */
function fitToBudget(hist: SharedHistoryDoc): void {
  while (hist.stack.length > 1 && utf8Bytes(JSON.stringify(hist)) > HISTORY_BYTES_BUDGET) {
    hist.stack.shift();
    hist.pointer = Math.max(0, hist.pointer - 1);
  }
}
const historyPath = (sessionId: string) => `sessions/${sessionId}/shared/history`;

interface SharedHistoryDoc {
  stack: (HistorySnapshot & { ts?: number; by?: string })[];
  pointer: number;
}

/**
 * The state the stack was left in - what the listener WOULD deliver.
 *
 * Returned so a client never has to be told the result of its own
 * write; see the note on the return statement below.
 */
export interface SharedHistoryPushResult {
  stack: (HistorySnapshot & { ts?: number; by?: string })[];
  pointer: number;
}

/** The stored history, or an empty stack when there is none or it is unreadable. */
function parseHistory(current: { data: string } | null): SharedHistoryDoc {
  if (!current) return { stack: [], pointer: -1 };
  try {
    return JSON.parse(current.data);
  } catch (_e) {
    return { stack: [], pointer: -1 }; // corrupted history data - start fresh
  }
}

/** What makes two snapshots the same state: the fields an undo restores. */
const snapshotKey = (s: Pick<HistorySnapshot, 'states' | 'narrativeLogCounts' | 'turnTracker'>) =>
  JSON.stringify({ states: s.states, narrativeLogCounts: s.narrativeLogCounts, turnTracker: s.turnTracker });

/** Is this state the one the pointer already rests on? */
function restsOn(hist: SharedHistoryDoc, key: string): boolean {
  if (hist.pointer < 0 || hist.pointer >= hist.stack.length) return false;
  return snapshotKey(hist.stack[hist.pointer]) === key;
}

/** Push one snapshot onto a freshly parsed stack, truncating any redo tail and capping the length. */
function pushSnapshot(hist: SharedHistoryDoc, snapshot: HistorySnapshot, playerId: string | null): void {
  // Baseline snapshots only seed an EMPTY stack (first-action undo).
  // If another client already pushed history, drop the baseline.
  if (snapshot.baseline && hist.stack.length > 0) return;
  const { baseline: _baseline, ...payload } = snapshot;
  if (restsOn(hist, snapshotKey(payload))) return;
  hist.stack = hist.stack.slice(0, hist.pointer + 1);
  hist.stack.push({ ...payload, ts: Date.now(), by: playerId || 'unknown' });
  if (hist.stack.length > MAX_SHARED_HISTORY) hist.stack.shift();
  hist.pointer = hist.stack.length - 1;
}

export const pushSharedHistoryBatch = async (
  sessionId: string,
  snapshots: HistorySnapshot[],
  playerId: string | null,
): Promise<SharedHistoryPushResult> => {
  // `mutate` MAY RUN MORE THAN ONCE under contention, so it must be pure:
  // `hist` is rebuilt from `current` on every attempt rather than carried in
  // from the closure.
  const written = await backend.data.transactDoc<{ data: string }>(historyPath(sessionId), (current) => {
    const hist = parseHistory(current);
    for (const snapshot of snapshots) pushSnapshot(hist, snapshot, playerId);
    fitToBudget(hist);
    return { data: JSON.stringify(hist) };
  });
  // THE WRITER IS TOLD BY ITSELF (v8.2.2). The caller used to learn the new
  // pointer only when the snapshot listener delivered it back - and that
  // listener can go quiet: in ~15% of two-client E2E runs it delivered its
  // initial snapshot and then nothing, while this very write committed. The
  // Undo button stayed disabled and the action could not be undone. A client
  // that just wrote the document has no reason to wait to be told what it
  // wrote, which is why transactDoc resolves with the value written.
  const saved: SharedHistoryDoc = written ? JSON.parse(written.data) : { stack: [], pointer: -1 };
  return { stack: saved.stack, pointer: saved.pointer };
};

export const onSharedHistoryUpdate = (
  sessionId: string,
  callback: (update: SharedHistoryUpdate) => void,
): Unsubscribe => {
  const EMPTY = { canUndo: false, canRedo: false, pointer: -1, length: 0, stack: [] };
  return watchJsonDoc<SharedHistoryDoc>('shared/history', historyPath(sessionId), (hist, exists) => {
    if (!exists || !hist) return callback(EMPTY);
    callback({
      canUndo: hist.pointer > 0,
      canRedo: hist.pointer < hist.stack.length - 1,
      pointer: hist.pointer,
      length: hist.stack.length,
      stack: hist.stack,
    });
  });
};

export interface HistoryPointerMove {
  pointer: number;
  entry: HistorySnapshot;
  stackLength: number;
}

/** The stack with its pointer moved by `delta`, and what the move landed on; null when it cannot move. */
function movedPointer(
  current: { data: string } | null,
  delta: number,
): { hist: SharedHistoryDoc; move: HistoryPointerMove } | null {
  if (!current) return null;
  let hist: SharedHistoryDoc;
  try {
    hist = JSON.parse(current.data);
  } catch (_e) {
    return null;
  }
  const newPointer = hist.pointer + delta;
  if (newPointer < 0 || newPointer >= hist.stack.length) return null;
  hist.pointer = newPointer;
  return { hist, move: { pointer: newPointer, entry: hist.stack[newPointer], stackLength: hist.stack.length } };
}

export const moveSharedHistoryPointer = async (
  sessionId: string,
  delta: number,
): Promise<HistoryPointerMove | null> => {
  // The caller wants a value DERIVED from the document, not the document, so
  // the last attempt records what it decided.
  //
  // This does not break the "mutate may run more than once" rule, which is
  // about not DEPENDING on a previous attempt: every line below recomputes
  // from `current`, and `outcome` is only ever written, never read. Whichever
  // attempt commits is the one that wrote it last.
  let outcome: HistoryPointerMove | null = null;
  await backend.data.transactDoc<{ data: string }>(historyPath(sessionId), (current) => {
    const moved = movedPointer(current, delta);
    outcome = moved?.move ?? null;
    return moved ? { data: JSON.stringify(moved.hist) } : undefined;
  });
  return outcome;
};

// ---- PLAYER CLEANUP ----

/**
 * Kick, and revoke membership with it.
 *
 * Both halves are `SessionPort.removeMember` - the same two writes were
 * spelled out here and in the adapter, and membership is the adapter's
 * business anyway: a `members` MAP with a field deleted out of it is
 * Firestore's shape, and PocketBase models the same thing as records.
 */
export const deletePlayer = (sessionId: string, playerId: string): Promise<void> =>
  retried(() => backend.sessions.removeMember(sessionId, playerId));

/**
 * A SEAT that went quiet, not a person to throw out (v8.310.4).
 *
 * The GM's stale-player prune used `deletePlayer`, which also REVOKES
 * membership - so five minutes of heartbeats lost to a throttled proxy
 * turned into every player kicked at once and unable to rejoin. The prune
 * only ever meant to tidy a presence doc nobody is heartbeating. This
 * removes the seat and nothing else; the person is still a member and
 * walks back in with a new seat.
 */
export const expirePresence = (sessionId: string, playerId: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(`sessions/${sessionId}/players/${playerId}`));

// ---- BATCH NARRATIVE (idempotent via deterministic doc IDs) ----

export const batchAddNarrativeEntries = (sessionId: string, entries: NarrativeEntryDoc[]): Promise<void> =>
  retried(() =>
    backend.data.batch(
      entries.map((entry) => ({
        op: 'set' as const,
        // Deterministic id - a retried batch overwrites rather than duplicates.
        path: `sessions/${sessionId}/narrative/${narrativeDocId(entry)}`,
        data: { ...entry, timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date() },
      })),
    ),
  );

// ---- BATCH TARGET SYNC ----

// ---- UNDO NARRATIVE CLEANUP ----

export const deleteNarrativeAfter = async (sessionId: string, watermarkMs: number): Promise<void> => {
  const entries = await backend.data.listDocs<{ timestamp?: unknown }>(`sessions/${sessionId}/narrative`);
  // No 499 cap. It used to `break` there, which silently left the rest of the
  // entries behind - an undo that looked like it worked and did not. Chunking
  // a batch past a backend's limit is the adapter's problem now.
  const doomed = entries.filter((e) => toMs(e.data.timestamp as never) > watermarkMs);
  if (!doomed.length) return;
  await retried(() =>
    backend.data.batch(doomed.map((e) => ({ op: 'delete' as const, path: `sessions/${sessionId}/narrative/${e.id}` }))),
  );
};

/**
 * Delete one run's own move line from the shared log (an undone charge, 2026-10-21): the entry whose beat is that
 * actor's move at that moment, and nothing else written meanwhile. One list, as `deleteNarrativeAfter` makes.
 */
export const deleteMoveLine = async (sessionId: string, line: { actorId: string; at: number }): Promise<void> => {
  const entries = await backend.data.listDocs<{ beat?: unknown }>(`sessions/${sessionId}/narrative`);
  const doomed = entries.filter((e) => isMoveLineOf(e.data.beat, line));
  if (!doomed.length) return;
  await retried(() =>
    backend.data.batch(doomed.map((e) => ({ op: 'delete' as const, path: `sessions/${sessionId}/narrative/${e.id}` }))),
  );
};

// ---- PENDING DEFENCE ----

export const onDefenceUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe => {
  return watchCollection('defence', `sessions/${sessionId}/defence`, callback);
};

export const setPendingDefence = (sessionId: string, tabId: string, data: Record<string, unknown>): Promise<void> =>
  retried(() => {
    return backend.data.setDoc(`sessions/${sessionId}/defence/${tabId}`, data);
  });

export interface DefenceResolution {
  resolvedBy: string;
  resolvedAt: number;
  choice: string;
  [key: string]: unknown;
}

// Resolve a pending defence via transaction (only first resolver wins)
/**
 * Why a claim came back empty - because the two reasons deserve different
 * answers (v8.310.17). `taken` is final: somebody resolved it first.
 * `missing` is usually TIME: the attacker's create is still in flight to
 * the server while the local view already shows the prompt, and a claim
 * that read "missing" as "gone" left the prompt on screen for ever, with
 * every choice answering the same way.
 */
type ClaimReason = 'won' | 'taken' | 'missing';
export interface DefenceClaim {
  resolution: DefenceResolution | null;
  reason: ClaimReason;
}

type Claimed = { reason: ClaimReason; resolved: DefenceResolution | null };

/** What one claim attempt decides, given the prompt as the server holds it now. */
function claimed(current: Record<string, unknown> | null, resolution: () => Record<string, unknown>): Claimed {
  if (!current) return { reason: 'missing', resolved: null };
  // Somebody else got there first. TRUTHINESS, not `!== null`: a Firestore
  // field holds the null it was written with, but a PocketBase `text`
  // column cannot store null and hands back "" - so the null test read
  // every unresolved prompt as already resolved, and no defence on that
  // backend could ever be answered. `DeathWatchSimulator` has always tested
  // this the truthy way; now both agree, and neither cares which backend it
  // is on.
  if (current.resolvedBy) return { reason: 'taken', resolved: null };
  return { reason: 'won', resolved: { ...current, ...resolution() } as DefenceResolution };
}

export const claimPendingDefence = async (
  sessionId: string,
  tabId: string,
  playerId: string,
  choice: string,
  /** Anything else the resolver wants on the record - the banner's table ruling (v8.366.0). */
  extra?: Record<string, unknown>,
): Promise<DefenceClaim> => {
  // FIRST RESOLVER WINS, and the compare has to happen inside the write. An
  // API rule guarding on the stored value looks like it would serve and does
  // not - measured against PocketBase, 5 rounds in 12 admitted two winners
  // (decisions-log 2026-08-11). `transactDoc` is the real thing on both
  // backends; `outcome` records what the committing attempt decided.
  let claim: Claimed = { reason: 'missing', resolved: null };
  await backend.data.transactDoc<Record<string, unknown>>(`sessions/${sessionId}/defence/${tabId}`, (current) => {
    claim = claimed(current, () => ({ resolvedBy: playerId, resolvedAt: Date.now(), choice, ...(extra ?? {}) }));
    return claim.resolved ? { ...claim.resolved } : undefined;
  });
  return { resolution: claim.resolved, reason: claim.reason };
};

/** The claim's resolution alone - null when it was taken or missing. */
export const resolvePendingDefence = async (
  sessionId: string,
  tabId: string,
  playerId: string,
  choice: string,
): Promise<DefenceResolution | null> => (await claimPendingDefence(sessionId, tabId, playerId, choice)).resolution;

export const clearPendingDefence = (sessionId: string, tabId: string): Promise<void> =>
  retried(() => {
    return backend.data.deleteDoc(`sessions/${sessionId}/defence/${tabId}`);
  });

export const clearAllPendingDefences = async (sessionId: string): Promise<void> => {
  const entries = await backend.data.listDocs(`sessions/${sessionId}/defence`);
  if (!entries.length) return;
  await retried(() =>
    backend.data.batch(entries.map((e) => ({ op: 'delete' as const, path: `sessions/${sessionId}/defence/${e.id}` }))),
  );
};

// ---- SHARED CHARACTER LIBRARY ----

export const onCharactersUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe => {
  return watchCollection('characters', `sessions/${sessionId}/characters`, callback);
};

export const setSharedCharacter = (
  sessionId: string,
  characterId: string,
  character: Character,
  playerId: string | null,
): Promise<void> =>
  retried(() => {
    return backend.data.setDoc(`sessions/${sessionId}/characters/${characterId}`, {
      data: JSON.stringify(character),
      name: character.name,
      updatedAt: new Date(),
      updatedBy: playerId || 'unknown',
      ownerUid: backend.auth.current()?.uid || 'unknown',
    });
  });

export const deleteSharedCharacter = (sessionId: string, characterId: string): Promise<void> =>
  retried(() => {
    return backend.data.deleteDoc(`sessions/${sessionId}/characters/${characterId}`);
  });

// Batch-push multiple characters (auto-split at 500-doc Firestore limit)
export const batchSetSharedCharacters = async (
  sessionId: string,
  characters: Character[],
  playerId: string | null,
): Promise<void> => {
  // No chunk loop. Firestore's 500-document limit is a property of one
  // backend, and the adapter that has it is the thing that should know.
  if (!characters.length) return;
  await retried(() =>
    backend.data.batch(
      characters.map((character) => ({
        op: 'set' as const,
        path: `sessions/${sessionId}/characters/${character.id}`,
        data: {
          data: JSON.stringify(character),
          name: character.name,
          updatedAt: new Date(),
          updatedBy: playerId || 'unknown',
          ownerUid: backend.auth.current()?.uid || 'unknown',
        },
      })),
    ),
  );
};

// ---- GM PERMISSIONS ----

/**
 * Set ONE house rule / lock / override without dropping everything else.
 *
 * The respread that makes these safe is pure and lives in
 * `engine/settings/mutate.ts`; all that is left here is the write.
 */
export function writeHouseRule(
  sessionId: string,
  permissions: PermissionsDoc | null | undefined,
  key: string,
  value: unknown,
): Promise<void> {
  return setPermissions(sessionId, withHouseRule(permissions, key, value));
}

export function writeLock(
  sessionId: string,
  permissions: PermissionsDoc | null | undefined,
  key: string,
  locked: boolean,
): Promise<void> {
  return setPermissions(sessionId, withLock(permissions, key, locked));
}

export function writeOverride(
  sessionId: string,
  permissions: PermissionsDoc | null | undefined,
  playerId: string,
  key: string,
  locked: boolean | null,
): Promise<void> {
  return setPermissions(sessionId, withOverride(permissions, playerId, key, locked));
}

// The settings document, its readers and its respread moved to
// `engine/settings/` in v8.90.0 - they are pure, and six components were
// importing this module only to reach them. Re-exported here so existing
// importers keep working; this facade goes when multiplayer.ts does.
export type { PermissionsDoc };
export * from './engine/settings/resolve';

export const setPermissions = async (sessionId: string, permissions: PermissionsDoc): Promise<void> => {
  // PermissionsDoc is an interface, so it needs widening to the port's
  // payload type - an interface has no index signature.
  await backend.data.setDoc(`sessions/${sessionId}/shared/permissions`, { ...permissions });
};

export const onPermissionsUpdate = (
  sessionId: string,
  callback: (perms: PermissionsDoc | null) => void,
): Unsubscribe => {
  return watchDocument<PermissionsDoc>('shared/permissions', `sessions/${sessionId}/shared/permissions`, (read) =>
    callback(read.exists ? read.data : null),
  );
};

// ---- TURN TRACKER ----

export const setSharedTurnTracker = (sessionId: string, turnTracker: TurnTrackerState): Promise<void> =>
  retried(() => {
    return backend.data.setDoc(`sessions/${sessionId}/shared/turnTracker`, { data: JSON.stringify(turnTracker) });
  });

/**
 * Rewrite the turn order INSIDE a compare-and-set (v8.310.6).
 *
 * `mutate` receives what the server holds now - null before the first
 * activation - and returns what it should hold. It may run more than once
 * on contention, so it must be pure; `useTurnTrackerSync` sends edits
 * replayed through `mergeTrackerChange`, which is. This is how a removal
 * survives a peer writing from an older copy: the peer's edit lands on the
 * document the removal already changed, not over it.
 */
/** The tracker a document holds; null when absent - or unreadable, which is replaced, not obeyed. */
function trackerIn(current: { data: string } | null): TurnTrackerState | null {
  if (!current?.data) return null;
  try {
    return JSON.parse(current.data) as TurnTrackerState;
  } catch {
    return null;
  }
}

export const transactSharedTurnTracker = (
  sessionId: string,
  mutate: (server: TurnTrackerState | null) => TurnTrackerState,
): Promise<TurnTrackerState | null> =>
  retried(async () => {
    const doc = await backend.data.transactDoc<{ data: string }>(
      `sessions/${sessionId}/shared/turnTracker`,
      (current) => ({ data: JSON.stringify(mutate(trackerIn(current))) }),
    );
    return doc?.data ? (JSON.parse(doc.data) as TurnTrackerState) : null;
  });

export const onSharedTurnTrackerUpdate = (
  sessionId: string,
  callback: SharedDocCallback<TurnTrackerState>,
): Unsubscribe => {
  return watchJsonDoc('shared/turnTracker', `sessions/${sessionId}/shared/turnTracker`, callback);
};

// ============================================================
// BATTLEFIELD (v7.16.0)
//
// Terrain lives in ONE shared document - it is edited deliberately and
// rarely, and an atomic swap is exactly what loading a map should be.
// Tokens live in a subcollection with a document each, so two people
// dragging different tokens merge server-side instead of racing over
// one blob.
//
// The GM-only terrain is a SECOND document rather than a `hidden` flag
// inside the first, because that is what makes fog of war enforceable:
// `isMember` grants read on everything under `shared/`, so a player with
// a console could read an ambush hidden client-side.
// ============================================================

/** The whole battlefield, as one JSON blob. */
export const setBattlefieldTerrain = (sessionId: string, terrain: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(`sessions/${sessionId}/shared/battlefield`, { data: JSON.stringify(terrain) }));

export const onBattlefieldTerrainUpdate = (sessionId: string, callback: SharedDocCallback<unknown>): Unsubscribe => {
  return watchJsonDoc('shared/battlefield', `sessions/${sessionId}/shared/battlefield`, callback);
};

/** GM-only terrain. Readable only by the GM - enforced in firestore.rules. */
export const setGmBattlefieldTerrain = (sessionId: string, terrain: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(`sessions/${sessionId}/battlefieldGm/terrain`, { data: JSON.stringify(terrain) }));

export const onGmBattlefieldTerrainUpdate = (sessionId: string, callback: SharedDocCallback<unknown>): Unsubscribe => {
  return watchJsonDoc('battlefieldGm/terrain', `sessions/${sessionId}/battlefieldGm/terrain`, callback);
};

/**
 * The optional fields that are set, in order. Firestore REJECTS A DOCUMENT
 * if any field is literally `undefined`, and the write fails async and only
 * logs (gotchas.md, v6.45.0) - so an unset optional is left out, never sent.
 */
function definedFields(fields: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined));
}

/** A token as stored: one document each under `battlefield/`. */
interface BattlefieldTokenDoc {
  id: string;
  kind: string;
  x: number;
  z: number;
  elevation: number;
  eyeHeight: number;
  footprint: number;
  rev: number;
  height?: number;
  ignoresTerrain?: boolean;
  heading?: number;
}

/**
 * A token's fields as stored.
 *
 * Fields are spread individually rather than passing the object through,
 * because Firestore REJECTS A DOCUMENT if any nested field is literally
 * `undefined` - and the write fails asynchronously, so sync dies silently
 * (gotchas.md, v6.45.0).
 */
const tokenFields = (token: BattlefieldTokenDoc): Record<string, unknown> => ({
  id: token.id,
  kind: token.kind,
  x: token.x,
  z: token.z,
  elevation: token.elevation,
  eyeHeight: token.eyeHeight,
  footprint: token.footprint,
  rev: token.rev,
  // EVERY FIELD A TOKEN CARRIES HAS TO BE LISTED HERE, and two were
  // not. A field left out is not merely unsaved: the write stores the
  // new `rev` without it, the snapshot comes back at that same rev,
  // and `acceptRemoteToken` takes the server's copy - so the value is
  // ERASED a moment after it is set. That is what made "this one is
  // flying" impossible to turn on, and it silently dropped a
  // Dreadnought's token height, which cover depends on.
  //
  // Spread conditionally: Firestore rejects a document if any field
  // is literally `undefined`.
  ...definedFields({ height: token.height, ignoresTerrain: token.ignoresTerrain, heading: token.heading }),
});

/**
 * Write one token: whole, or - ONTO A TOKEN THAT MUST EXIST - as an update
 * (2026-10-17). A screen still holding someone the GM had just struck wrote
 * their token before hearing, and the whole write created it again on the
 * server. `mustExist` (a view has shown the token here) makes it an
 * `updateDoc`, which writes nothing onto a token that is gone. An update
 * keeps a field it is not sent where a whole write would clear it, so it
 * says `ignoresTerrain` outright: the one optional field that goes from set
 * to unset (a replaced person who does not fly).
 */
export const setBattlefieldToken = (sessionId: string, token: BattlefieldTokenDoc, mustExist = false): Promise<void> =>
  retried(async () => {
    const path = `sessions/${sessionId}/battlefield/${token.id}`;
    if (mustExist)
      await backend.data.updateDoc(path, { ...tokenFields(token), ignoresTerrain: token.ignoresTerrain === true });
    else await backend.data.setDoc(path, tokenFields(token));
  });

export const deleteBattlefieldToken = (sessionId: string, tokenId: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(`sessions/${sessionId}/battlefield/${tokenId}`));

/**
 * A ping - speech aimed at a point on the ground.
 *
 * Shares the `battlefield` collection with tokens, discriminated by
 * `kind`, which the token listener has always filtered on. That is why
 * pings needed no rule of their own: `firestore.rules` has said
 * "tokens, markers and pings" over this path since it was written.
 *
 * EVERY FIELD LISTED, and the optional two spread conditionally:
 * Firestore rejects a document outright if any field is literally
 * `undefined`, the write fails async and only logs, and the ping would
 * simply never appear (gotchas, v6.45.0).
 */
export const setBattlefieldPing = (
  sessionId: string,
  ping: {
    id: string;
    kind: 'ping';
    type: string;
    x: number;
    z: number;
    y?: number;
    by: string;
    byName: string;
    at: number;
    note?: string;
    fromId?: string;
  },
): Promise<void> =>
  retried(() =>
    backend.data.setDoc(`sessions/${sessionId}/battlefield/${ping.id}`, {
      id: ping.id,
      kind: 'ping',
      type: ping.type,
      x: ping.x,
      z: ping.z,
      ...definedFields({ y: ping.y }),
      by: ping.by,
      byName: ping.byName,
      at: ping.at,
      ...definedFields({ note: ping.note, fromId: ping.fromId }),
    }),
  );

/**
 * Tidy up after a ping that has said its piece.
 *
 * Not what makes it disappear - every client stops drawing it on its own
 * clock, so a sender who closed their tab cannot leave a ring on anybody's
 * screen. This only stops the collection growing for ever.
 */
export const deleteBattlefieldPing = (sessionId: string, pingId: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(`sessions/${sessionId}/battlefield/${pingId}`));

export const onBattlefieldTokensUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe => {
  return watchCollection('battlefield', `sessions/${sessionId}/battlefield`, callback);
};

/**
 * The session's saved battlefields, A DOCUMENT PER MAP (v8.310.19).
 *
 * They were one JSON blob under `shared/battlefieldLibrary`. An imported
 * campaign brought a 10 MB one, Firestore's ceiling is 1 MiB, the SDK
 * retries a refused write for ever, and every write queued behind it -
 * tokens, combatants, reaction prompts, heartbeats - never landed. A map
 * is its own document now, named by `libraryDocId(name)`, so an oversized
 * map is refused on its own with a sentence and takes nothing down with
 * it. The blob is still READ, once, so a session from before this keeps
 * its library until the first write moves it across.
 */
/**
 * One map, as its documents: packed, and split into parts when it is
 * still bigger than one document (`splitLibraryEntry`). Written as ONE
 * batch with the deletes of any parts the previous version occupied and
 * this one does not, so a map never reads torn between its old and new
 * shape. Returns the ids written, which is what the next rewrite or a
 * delete has to know.
 */
export const writeMapLibraryEntry = async (
  sessionId: string,
  entry: LibraryEntry,
  previousDocIds: readonly string[] = [],
  playerId: string | null = null,
): Promise<string[]> => {
  const docs = splitLibraryEntry(entry);
  const ids = new Set(docs.map((d) => d.id));
  const at = `sessions/${sessionId}/mapLibrary`;
  const updatedAt = Date.now();
  await retried(() =>
    backend.data.batch([
      ...docs.map((d) => ({
        op: 'set' as const,
        path: `${at}/${d.id}`,
        data: { ...d, updatedAt, ...(playerId ? { updatedBy: playerId } : {}) },
      })),
      ...previousDocIds.filter((id) => !ids.has(id)).map((id) => ({ op: 'delete' as const, path: `${at}/${id}` })),
    ]),
  );
  return [...ids];
};

/** Every document a map occupies - the listener's `docIds`, or the bare id for a map never seen listed. */
export const deleteMapLibraryEntry = (sessionId: string, name: string, docIds?: readonly string[]): Promise<void> =>
  retried(() =>
    backend.data.batch(
      (docIds?.length ? docIds : [libraryDocId(name)]).map((id) => ({
        op: 'delete' as const,
        path: `sessions/${sessionId}/mapLibrary/${id}`,
      })),
    ),
  );

export const onMapLibraryUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe =>
  watchCollection('mapLibrary', `sessions/${sessionId}/mapLibrary`, callback);

/** The pre-v8.310.19 blob, deleted once its maps have been written one by one. */
export const deleteLegacyBattlefieldLibrary = (sessionId: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(`sessions/${sessionId}/shared/battlefieldLibrary`));

/**
 * The deployment marker and its ring.
 *
 * SESSION state, deliberately - not part of the terrain document. That is
 * what lets it survive a map swap: loading a map cannot touch a document
 * it does not own. A saved map may still carry a suggested marker, which
 * is applied only when the session has none.
 *
 * Stored as one JSON string like the map library, rather than as fields:
 * `RingConfig` is nested, and Firestore rejects a document outright if any
 * nested field is literally `undefined` (gotchas.md, v6.45.0).
 */
/** Each value parsed as JSON; one that will not parse is left out, not fatal. */
function parseEach(values: unknown[]): unknown[] {
  const out: unknown[] = [];
  for (const value of values) {
    try {
      out.push(JSON.parse(value as string));
    } catch {
      /* one unreadable record is not a broken rail */
    }
  }
  return out;
}

/**
 * A GM-only collection of `{ data: "<json>" }` records, parsed, on every change.
 *
 * A PLAYER WITHOUT THE GRANT GETS AN ERROR, NOT AN EMPTY LIST - the
 * rules refuse the read, which is the point. `resilientListener` would
 * otherwise retry a permission denial forever, so a denial resolves to
 * "none" and stops rather than being treated as a fault.
 */
const watchSecretRecords = (label: string, path: string, callback: (records: unknown[]) => void): Unsubscribe => {
  // Through `delivered` like every other listener, so the E2E mute reaches
  // the GM's secret lists too (v8.398.6) - they went on hearing through it.
  const records = (read: CollectionRead<{ data?: string }>) => callback(parseEach(read.docs.map((doc) => doc.data)));
  return forgetOnStop(
    records,
    resilientListener(label, (onError, onHealthy) =>
      backend.data.watchCollection<{ data?: string }>(path, delivered(onHealthy, records), (err: unknown) => {
        // Not a member of this secret: say so once, quietly, and stop. Asked of the PORT's vocabulary, not of
        // Firestore's code: on PocketBase a refusal is a 403, and read as a fault it took the revoke path
        // (the multiplayer audit, 2026-10-31).
        if (backend.errorKind(err) === 'denied') return callback([]);
        onError(err);
      }),
    ),
  );
};

// ---- WAITING ROOMS (GM-only) ----
//
// A DOCUMENT PER ROOM, not one document holding them all. A parked
// combatant carries its whole sheet, so a room of thirty is already
// substantial and Firestore's 1 MiB per document is a real ceiling -
// and two rooms being filled at once should not race over one blob, for
// exactly the reason tokens are a document each.
//
// The payload is a JSON string like the deployment marker's, because a
// parked combatant is deeply nested and Firestore rejects a document
// with a literal `undefined` anywhere inside it (gotchas, v6.45.0).
//
// `gmWaiting` rather than under `shared`: `isMember` grants read on
// everything under `shared`, so the concealment has to live somewhere
// the rules can refuse. See firestore.rules.

export const setWaitingRoom = (sessionId: string, room: { id: string }): Promise<void> =>
  retried(() =>
    backend.data.setDoc(`sessions/${sessionId}/gmWaiting/${room.id}`, {
      data: JSON.stringify(room),
      updatedAt: Date.now(),
    }),
  );

export const deleteWaitingRoom = (sessionId: string, roomId: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(`sessions/${sessionId}/gmWaiting/${roomId}`));

/** Every room, on every change. A player without the grant hears "no rooms", once. */
export const onWaitingRoomsUpdate = (sessionId: string, callback: (rooms: unknown[]) => void): Unsubscribe =>
  watchSecretRecords('gmWaiting', `sessions/${sessionId}/gmWaiting`, callback);

// ---- SCENES - THE WINGS (GM-only) ----
//
// Three collections, all `gm`, none of them large: the RECORD (name, marks,
// tracker, marker - a few KB), ONE DOCUMENT PER PARKED COMBATANT (a cast of
// thirty in one blob is exactly the room-of-thirty ceiling, gotcha 12), and
// the TERRAIN packed and split into parts with the map library's codec. The
// only member-readable fact is `shared/stage`: which scene is on, and
// whether the curtain is down. See engine/scenes/types.ts for the rules.

export const setScene = (sessionId: string, record: { id: string; name: string; order: number }): Promise<void> =>
  retried(() =>
    backend.data.setDoc(`sessions/${sessionId}/scenes/${record.id}`, {
      data: JSON.stringify(record),
      name: record.name,
      order: record.order,
      updatedAt: Date.now(),
    }),
  );

export const deleteScene = (sessionId: string, sceneId: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(`sessions/${sessionId}/scenes/${sceneId}`));

/** Every scene record, on every change. A player's denial is "no scenes", as the rooms'. */
export const onScenesUpdate = (sessionId: string, callback: (records: unknown[]) => void): Unsubscribe =>
  watchSecretRecords('scenes', `sessions/${sessionId}/scenes`, callback);

/**
 * Park a cast: one document each, and the deletes of whoever was parked
 * here last time and is not now - ONE BATCH, so a re-park never reads torn.
 */
export const writeSceneCast = (
  sessionId: string,
  sceneId: string,
  cast: readonly { id: string }[],
  removeIds: readonly string[] = [],
): Promise<void> => {
  const at = `sessions/${sessionId}/sceneCast`;
  const updatedAt = Date.now();
  const keep = new Set(cast.map((c) => c.id));
  return retried(() =>
    backend.data.batch([
      ...cast.map((c) => ({
        op: 'set' as const,
        path: `${at}/${castDocId(sceneId, c.id)}`,
        data: { scene: sceneId, data: JSON.stringify(c), updatedAt },
      })),
      ...removeIds
        .filter((id) => !keep.has(id))
        .map((id) => ({ op: 'delete' as const, path: `${at}/${castDocId(sceneId, id)}` })),
    ]),
  );
};

/**
 * The parked cast, by the record's `castIds` - exact reads, never a listing
 * of every scene's cast. A document that is missing or unreadable is left
 * out: the record's list is the intent, the documents are what survived.
 */
export const readSceneCast = async (
  sessionId: string,
  sceneId: string,
  castIds: readonly string[],
): Promise<unknown[]> => {
  const reads = await Promise.all(
    castIds.map((id) =>
      backend.data.getDoc<{ data?: string }>(`sessions/${sessionId}/sceneCast/${castDocId(sceneId, id)}`),
    ),
  );
  // One unreadable person is not a lost scene.
  return parseEach(reads.filter((read) => read.exists && read.data?.data).map((read) => read.data?.data));
};

export const deleteSceneCast = (sessionId: string, sceneId: string, combatantIds: readonly string[]): Promise<void> =>
  combatantIds.length
    ? retried(() =>
        backend.data.batch(
          combatantIds.map((id) => ({
            op: 'delete' as const,
            path: `sessions/${sessionId}/sceneCast/${castDocId(sceneId, id)}`,
          })),
        ),
      )
    : Promise.resolve();

/**
 * A scene's terrain: the map library's codec, keyed by the scene id, in a
 * GM-only collection. Returns the document ids written, which the next
 * rewrite or the delete has to know.
 */
export const writeSceneTerrain = async (
  sessionId: string,
  sceneId: string,
  terrain: LibraryEntry['terrain'],
  previousDocIds: readonly string[] = [],
): Promise<string[]> => {
  const docs = splitLibraryEntry({ name: sceneId, terrain });
  const ids = new Set(docs.map((d) => d.id));
  const at = `sessions/${sessionId}/sceneTerrain`;
  const updatedAt = Date.now();
  await retried(() =>
    backend.data.batch([
      ...docs.map((d) => ({ op: 'set' as const, path: `${at}/${d.id}`, data: { ...d, updatedAt } })),
      ...previousDocIds.filter((id) => !ids.has(id)).map((id) => ({ op: 'delete' as const, path: `${at}/${id}` })),
    ]),
  );
  return [...ids];
};

/** The terrain back, assembled from its parts - part 0 says how many there are. Null when none. */
export const readSceneTerrain = async (
  sessionId: string,
  sceneId: string,
): Promise<{ terrain: LibraryEntry['terrain'] | null; docIds: string[] }> => {
  const at = `sessions/${sessionId}/sceneTerrain`;
  const first = await backend.data.getDoc<LibraryDoc>(`${at}/${partDocId(sceneId, 0)}`);
  if (!first.exists || !first.data) return { terrain: null, docIds: [] };
  const count = Math.max(1, Number(first.data.parts) || 1);
  const rest = await Promise.all(
    Array.from({ length: count - 1 }, (_, i) => backend.data.getDoc<LibraryDoc>(`${at}/${partDocId(sceneId, i + 1)}`)),
  );
  const docs = [first, ...rest].flatMap((r, i) =>
    r.exists && r.data ? [{ ...r.data, id: partDocId(sceneId, i) }] : [],
  );
  const assembled = assembleLibrary(docs);
  const entry = assembled.entries.find((e) => e.name === sceneId) ?? null;
  return { terrain: entry?.terrain ?? null, docIds: assembled.docIds.get(sceneId) ?? [] };
};

export const deleteSceneTerrain = (sessionId: string, sceneId: string, docIds?: readonly string[]): Promise<void> =>
  retried(() =>
    backend.data.batch(
      (docIds?.length ? docIds : [partDocId(sceneId, 0)]).map((id) => ({
        op: 'delete' as const,
        path: `sessions/${sessionId}/sceneTerrain/${id}`,
      })),
    ),
  );

/** `shared/stage` - what the table is looking at. Member-writable like the marker; the wings are the secret. */
export const setStage = (sessionId: string, stage: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(`sessions/${sessionId}/shared/stage`, { data: JSON.stringify(stage) }));

export const onStageUpdate = (sessionId: string, callback: SharedDocCallback<unknown>): Unsubscribe =>
  watchJsonDoc('shared/stage', `sessions/${sessionId}/shared/stage`, callback);

/** A `{ data: "<json>" }` document's value, or null for a blob that does not parse - which reads as absent. */
function parsedData(data: { data?: string } | null): unknown {
  try {
    return JSON.parse(data?.data as string);
  } catch {
    return null;
  }
}

// ---- Asks (one seat asking another, 2026-10-05) ----

const askPath = (sessionId: string, id: string): string => `sessions/${sessionId}/asks/${id}`;

export const onAsksUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe =>
  watchCollection('asks', `sessions/${sessionId}/asks`, callback);

/** Write a new ask. Its id is the asker's to mint. */
export const putAsk = (sessionId: string, ask: { id: string }): Promise<void> =>
  retried(() => backend.data.setDoc(askPath(sessionId, ask.id), { data: JSON.stringify(ask) }));

/**
 * Move an ask on, as a compare-and-set: `transition` is handed the STORED
 * ask and returns the next one, or null to leave it alone (already answered,
 * already cancelled, or gone). Resolves true when it wrote.
 */
export async function transitionAsk(
  sessionId: string,
  id: string,
  transition: (stored: unknown) => unknown | null,
): Promise<boolean> {
  let wrote = false;
  await backend.data.transactDoc<{ data: string }>(askPath(sessionId, id), (current) => {
    if (!current) return undefined;
    const next = transition(parsedData(current));
    wrote = next !== null;
    return next === null ? undefined : { data: JSON.stringify(next) };
  });
  return wrote;
}

export const deleteAsk = (sessionId: string, id: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(askPath(sessionId, id)));

// ---- Clocks and the board (2026-10-05): the GM's documents, shown by audience ----

/** The collections a GM writes one document per thing into, each shown or kept by its `audience`. */
export type ShownCollection = 'clocks' | 'board';

/**
 * What the GM has shown me - or, for the GM (`uid` null), everything. As
 * with handouts, the player's query is the audience the rules can prove,
 * and a reveal can be taken back, so that view re-lists.
 */
export const onShownUpdate = (
  name: ShownCollection,
  sessionId: string,
  uid: string | null,
  callback: CollectionCallback,
): Unsubscribe =>
  uid === null
    ? watchCollection(name, `sessions/${sessionId}/${name}`, callback)
    : watchCollectionWhere(
        name,
        `sessions/${sessionId}/${name}`,
        { field: 'audience', anyOf: ['*', '@players', uid], narrows: true },
        callback,
      );

export const putShown = (
  name: ShownCollection,
  sessionId: string,
  id: string,
  doc: { data: string; audience: string[] },
): Promise<void> => retried(() => backend.data.setDoc(`sessions/${sessionId}/${name}/${id}`, doc));

export const deleteShown = (name: ShownCollection, sessionId: string, id: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(`sessions/${sessionId}/${name}/${id}`));

// ---- The GM's desk (2026-10-05): readable by the GM alone, by rule ----

const notesPath = (sessionId: string): string => `sessions/${sessionId}/gmDesk/notes`;

/** The GM's notes as stored: the text, or null when none were ever written. Only the GM may listen. */
export const onGmNotesUpdate = (sessionId: string, callback: (text: string | null) => void): Unsubscribe =>
  watchDocument<{ data?: string }>('gmDesk/notes', notesPath(sessionId), (read) => {
    const stored = read.exists ? (parsedData(read.data) as { text?: unknown } | null) : null;
    callback(typeof stored?.text === 'string' ? stored.text : null);
  });

export const setGmNotes = (sessionId: string, text: string): Promise<void> =>
  retried(() => backend.data.setDoc(notesPath(sessionId), { data: JSON.stringify({ text }) }));

/**
 * One value on the GM's desk, by name: the hidden half of something whose
 * other half the table sees (how well an approach went). Null when it was
 * never written, or is not readable. Only the GM may listen or write.
 */
export const onGmDeskUpdate = (sessionId: string, id: string, callback: (value: unknown) => void): Unsubscribe =>
  watchDocument<{ data?: string }>(`gmDesk/${id}`, `sessions/${sessionId}/gmDesk/${id}`, (read) => {
    callback(read.exists ? parsedData(read.data) : null);
  });

export const setGmDesk = (sessionId: string, id: string, value: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(`sessions/${sessionId}/gmDesk/${id}`, { data: JSON.stringify(value) }));

// ---- Open calls for a test (v9.5.3): the GM's screen keeps each while it waits ----

const callPath = (sessionId: string, id: string): string => `sessions/${sessionId}/testCalls/${id}`;

/** Every open call, as stored: `{ id, data }` reads. Only the GM may listen. */
export const onTestCallsUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe =>
  watchCollection('testCalls', `sessions/${sessionId}/testCalls`, callback);

export const putTestCall = (sessionId: string, id: string, value: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(callPath(sessionId, id), { data: JSON.stringify(value), updatedAt: Date.now() }));

export const deleteTestCall = (sessionId: string, id: string): Promise<void> =>
  retried(() => backend.data.deleteDoc(callPath(sessionId, id)));

// ---- Hidden figures (plan map-display, G1): one GM-only document per figure ----

const hiddenPath = (sessionId: string, id: string): string => `sessions/${sessionId}/gmHidden/${id}`;

/** Every hidden figure, as stored: `{ id, data }` reads, removals included. Only the GM may listen. */
export const onHiddenFiguresUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe =>
  watchCollection('gmHidden', `sessions/${sessionId}/gmHidden`, callback);

/** Write one hidden figure's document (`figureToDoc`: the figure as text). */
export const setHiddenFigure = (
  sessionId: string,
  id: string,
  doc: { data: string; updatedAt: number },
): Promise<void> => retried(() => backend.data.setDoc(hiddenPath(sessionId, id), doc));

// ---- Handouts' seen marks (v9.8.0): one document per person, written by its owner alone ----

/** Everyone's marks, as stored: `{ id, data }` reads. Any member may listen; the rules let each write only their own. */
export const onHandoutSeenUpdate = (sessionId: string, callback: CollectionCallback): Unsubscribe =>
  watchCollection('handoutSeen', `sessions/${sessionId}/handoutSeen`, callback);

/** `doc` is engine/handouts `seenDoc`: `uid` names its owner, which is what the rules check. */
export const setHandoutSeen = (sessionId: string, uid: string, doc: Record<string, unknown>): Promise<void> =>
  retried(() => backend.data.setDoc(`sessions/${sessionId}/handoutSeen/${uid}`, doc));

// ---- A browser's diagnostics (2026-10-31, plan sync-listeners): one document per person, read by the GM ----

/** What a seat uploads: its last lines and when. Stored with `uid`, its owner, which is what the rules check. */
export interface SeatDiag {
  diag: string;
  diagAt: number;
}

/**
 * Upload this browser's last lines under its own uid. Off the seat record: on it, every heartbeat delivered up to
 * eighty lines per seat to every screen. A failure is the caller's to note (the uploader marks its lines dirty).
 */
export const setSeatDiag = (sessionId: string, uid: string, doc: SeatDiag): Promise<void> =>
  backend.data.setDoc(`sessions/${sessionId}/seatDiag/${uid}`, { uid, ...doc });

/**
 * Every seat's last lines, by uid - read ONCE, when the GM copies a bug report; nobody listens. The rules let the GM
 * read them all; anyone else gets their own at most, and a refusal is an empty table, not a lost report.
 */
export const readSeatDiags = async (sessionId: string): Promise<Record<string, SeatDiag>> => {
  try {
    const entries = await backend.data.listDocs<Partial<SeatDiag> & { uid?: string }>(`sessions/${sessionId}/seatDiag`);
    return Object.fromEntries(
      entries.map(({ id, data }) => [
        data.uid ?? id,
        { diag: String(data.diag ?? ''), diagAt: Number(data.diagAt) || 0 },
      ]),
    );
  } catch (err) {
    diag.warn('diag', "the seats' lines could not be read for the report", errorSummary(err));
    return {};
  }
};

const framePath = (sessionId: string): string => `sessions/${sessionId}/direction/frame`;

/** `direction/frame` - the session phase. The rules admit the GM alone. */
export const setFrame = (sessionId: string, frame: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(framePath(sessionId), { data: JSON.stringify(frame) }));

/**
 * Follow `direction/frame`: the stored frame, or `null` once the SERVER has
 * said there is none.
 *
 * ABSENCE IS CONFIRMED BY A READ, never taken from the watch. Firestore's
 * first snapshot of a document it has not fetched yet is the cache's guess -
 * `exists: false` - and when the server then agrees, no second snapshot comes
 * (a metadata-only change). A watch alone cannot tell "no frame" from "not
 * read yet", and the table would flash Combat before the real phase. So
 * until an answer arrives the callback is simply not called.
 */
export const onFrameUpdate = (sessionId: string, callback: (frame: unknown | null) => void): Unsubscribe => {
  const path = framePath(sessionId);
  let live = true;
  let present = false;
  const confirmAbsent = () =>
    backend.data.getDoc(path).then(
      (read) => {
        if (live && !present && !read.exists) callback(null);
      },
      // Unreachable server: stay unknown. The caller has a ceiling on how long it waits.
      () => {},
    );
  const stop = watchDocument<{ data?: string }>('direction/frame', path, (read) => {
    present = read.exists;
    if (!read.exists) return void confirmAbsent();
    callback(parsedData(read.data));
  });
  return () => {
    live = false;
    stop();
  };
};

// ---- Squad Mode (2026-10-23): the GM's acting-leader answers and the kill-teams' Cohesion ----

const teamsPath = (sessionId: string): string => `sessions/${sessionId}/direction/teams`;

/** `direction/teams` - the GM's acting-leader answers (engine/killTeam/record; the kill-teams are on the sheets). The GM alone writes. */
export const setTeamsDoc = (sessionId: string, doc: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(teamsPath(sessionId), { data: JSON.stringify(doc) }));

/** Follow `direction/teams`: the stored document, or null when there is none (no kill-team set up yet). */
export const onTeamsDocUpdate = (sessionId: string, callback: (doc: unknown | null) => void): Unsubscribe =>
  watchDocument<{ data?: string }>('direction/teams', teamsPath(sessionId), (read) =>
    callback(read.exists ? parsedData(read.data) : null),
  );

const cohesionPath = (sessionId: string): string => `sessions/${sessionId}/shared/cohesion`;

/** The pools as stored, parsed; null for none or an unreadable one. */
function cohesionIn(current: { data?: string } | null): unknown | null {
  if (!current?.data) return null;
  try {
    return JSON.parse(current.data);
  } catch {
    return null;
  }
}

/**
 * Every Cohesion transition is a compare-and-set on `shared/cohesion` (member-writable: any brother spends it).
 * `mutate` gets the stored pools (null: none yet) and returns the next, or undefined to write nothing - a refusal.
 * Resolves with what was written, null when nothing was.
 */
export const transactCohesion = (sessionId: string, mutate: (server: unknown | null) => unknown | undefined) =>
  retried(async () => {
    const doc = await backend.data.transactDoc<{ data: string }>(cohesionPath(sessionId), (current) => {
      const next = mutate(cohesionIn(current));
      return next === undefined ? undefined : { data: JSON.stringify(next) };
    });
    return cohesionIn(doc);
  });

/**
 * Follow `shared/cohesion`: the stored pools, and whether the document EXISTS - a failed read goes to the watch's error
 * path, never here; an unreadable one is reported as absent (`watchJsonDoc`). Absent is no pools, never a pool of 0.
 */
export const onCohesionUpdate = (sessionId: string, callback: SharedDocCallback<unknown>): Unsubscribe =>
  watchJsonDoc('shared/cohesion', cohesionPath(sessionId), callback);

const cutPath = (sessionId: string): string => `sessions/${sessionId}/direction/cut`;

/** `direction/cut` - the GM's cut: every display camera to one figure (plan map-display, C). The GM alone writes. */
export const setCameraCut = (sessionId: string, cut: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(cutPath(sessionId), { data: JSON.stringify(cut) }));

/** Follow `direction/cut`: the stored cut, or null when there is none. */
export const onCameraCutUpdate = (sessionId: string, callback: (cut: unknown | null) => void): Unsubscribe =>
  watchDocument<{ data?: string }>('direction/cut', cutPath(sessionId), (read) =>
    callback(read.exists ? parsedData(read.data) : null),
  );

/**
 * Empty the shared undo stack. A cut's only caller: a snapshot taken on
 * one stage restored onto another would put the other scene's wounds and
 * order on this one.
 */
export const clearSharedHistory = (sessionId: string): Promise<void> =>
  retried(() => backend.data.setDoc(historyPath(sessionId), { data: JSON.stringify({ stack: [], pointer: -1 }) }));

export const setDeployment = (sessionId: string, deployment: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(`sessions/${sessionId}/shared/deployment`, { data: JSON.stringify(deployment) }));

export const onDeploymentUpdate = (sessionId: string, callback: SharedDocCallback<unknown>): Unsubscribe => {
  return watchJsonDoc('shared/deployment', `sessions/${sessionId}/shared/deployment`, callback);
};

/**
 * Each team's own arrival point (plan scene-editor-library, B): a document of its own beside `shared/deployment`, so a
 * client that knows nothing of team points reads and writes the main marker exactly as before. `{}` is none.
 */
export const setArrivals = (sessionId: string, arrivals: unknown): Promise<void> =>
  retried(() => backend.data.setDoc(`sessions/${sessionId}/shared/arrivals`, { data: JSON.stringify(arrivals) }));

export const onArrivalsUpdate = (sessionId: string, callback: SharedDocCallback<unknown>): Unsubscribe =>
  watchJsonDoc('shared/arrivals', `sessions/${sessionId}/shared/arrivals`, callback);

export const onBattlefieldLibraryUpdate = (sessionId: string, callback: SharedDocCallback<unknown>): Unsubscribe => {
  return watchJsonDoc('shared/battlefieldLibrary', `sessions/${sessionId}/shared/battlefieldLibrary`, callback);
};
